import { theme } from 'design-cannect'
import styled from 'styled-components'

export const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  width: 100%;
  min-height: 100vh;
  padding: 1.5rem 1rem 4rem;
  background-color: #f7f8fa;
`

export const ContentWrapper = styled.div`
  width: 100%;
  max-width: 720px;

  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`

export const Header = styled.header`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  text-align: center;
  margin-bottom: 0.5rem;

  h1 {
    font-size: 1.5rem;
    font-weight: 700;
    color: ${theme.colors.tertiary};
    line-height: 1.3;
  }

  p {
    color: #4b5563;
    font-size: 0.95rem;
    line-height: 1.5;
  }

  @media (min-width: 768px) {
    h1 {
      font-size: 1.75rem;
    }
  }
`

export const Card = styled.section`
  background-color: #ffffff;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  padding: 1.25rem 1rem;

  display: flex;
  flex-direction: column;
  gap: 1.25rem;

  @media (min-width: 768px) {
    padding: 1.5rem;
  }
`

export const SectionTitle = styled.h2`
  font-size: 1.05rem;
  font-weight: 700;
  color: ${theme.colors.tertiary};
  margin: 0;
`

export const QuestionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

export const QuestionLabel = styled.label`
  font-size: 0.95rem;
  font-weight: 600;
  color: #1f2937;
  line-height: 1.4;

  span.required {
    color: #dc2626;
    margin-left: 2px;
  }
`

export const HelpText = styled.span`
  font-size: 0.8rem;
  color: #6b7280;
`

export const TextInput = styled.input`
  width: 100%;
  padding: 0.75rem 0.875rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.95rem;
  color: #1f2937;
  background-color: #ffffff;
  outline: none;
  transition: border-color 0.15s;

  &:focus {
    border-color: ${theme.colors.tertiary};
    box-shadow: 0 0 0 3px rgba(30, 64, 175, 0.1);
  }
`

export const TextArea = styled.textarea`
  width: 100%;
  min-height: 100px;
  padding: 0.75rem 0.875rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.95rem;
  color: #1f2937;
  background-color: #ffffff;
  outline: none;
  resize: vertical;
  font-family: inherit;
  transition: border-color 0.15s;

  &:focus {
    border-color: ${theme.colors.tertiary};
    box-shadow: 0 0 0 3px rgba(30, 64, 175, 0.1);
  }
`

export const ChoiceList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

export const OptionExtraTextInput = styled.input`
  width: 100%;
  padding: 0.625rem 0.875rem;
  margin-top: 0.125rem;
  margin-left: 1.75rem;
  width: calc(100% - 1.75rem);
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.9rem;
  color: #1f2937;
  background-color: #f9fafb;
  outline: none;
  transition: border-color 0.15s;
  font-family: inherit;

  &:focus {
    border-color: ${theme.colors.tertiary};
    box-shadow: 0 0 0 3px rgba(30, 64, 175, 0.1);
    background-color: #ffffff;
  }

  &::placeholder {
    color: #9ca3af;
  }
`

export const ChoiceItem = styled.label<{ selected?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.625rem 0.875rem;
  border-radius: 8px;
  border: 1px solid ${({ selected }) => (selected ? theme.colors.tertiary : '#d1d5db')};
  background-color: ${({ selected }) => (selected ? 'rgba(30, 64, 175, 0.06)' : '#ffffff')};
  cursor: pointer;
  font-size: 0.95rem;
  color: #1f2937;
  transition:
    background-color 0.15s,
    border-color 0.15s;

  &:hover {
    border-color: ${theme.colors.tertiary};
  }

  input {
    width: 18px;
    height: 18px;
    cursor: pointer;
    accent-color: ${theme.colors.tertiary};
  }
`

export const StarsRow = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;

  @media (min-width: 480px) {
    gap: 0.75rem;
  }
`

export const StarButton = styled.button<{ active?: boolean }>`
  width: 52px;
  height: 52px;
  display: flex;
  align-items: center;
  justify-content: center;

  background: transparent;
  border: none;
  border-radius: 8px;
  padding: 0;
  cursor: pointer;
  outline: none;

  color: ${({ active }) => (active ? '#f59e0b' : '#d1d5db')};
  transition:
    transform 0.1s,
    color 0.15s;

  svg {
    width: 100%;
    height: 100%;
    filter: ${({ active }) => (active ? 'drop-shadow(0 1px 2px rgba(245, 158, 11, 0.3))' : 'none')};
  }

  &:hover {
    transform: scale(1.05);
  }

  &:focus-visible {
    box-shadow: 0 0 0 3px rgba(30, 64, 175, 0.2);
  }

  @media (min-width: 480px) {
    width: 56px;
    height: 56px;
  }
`

export const NpsScaleRow = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 0.375rem;

  @media (min-width: 480px) {
    grid-template-columns: repeat(11, 1fr);
  }
`

export const NpsScaleButton = styled.button<{ active?: boolean; tone: 'red' | 'yellow' | 'green' }>`
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  background-color: ${({ active, tone }) => {
    if (!active) return '#ffffff'
    if (tone === 'red') return '#fee2e2'
    if (tone === 'yellow') return '#fef3c7'
    return '#d1fae5'
  }};
  color: ${({ active, tone }) => {
    if (!active) return '#374151'
    if (tone === 'red') return '#991b1b'
    if (tone === 'yellow') return '#92400e'
    return '#065f46'
  }};
  border-color: ${({ active, tone }) => {
    if (!active) return '#d1d5db'
    if (tone === 'red') return '#fca5a5'
    if (tone === 'yellow') return '#fcd34d'
    return '#6ee7b7'
  }};
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    border-color: ${({ tone }) => {
      if (tone === 'red') return '#fca5a5'
      if (tone === 'yellow') return '#fcd34d'
      return '#6ee7b7'
    }};
  }
`

export const ErrorMessage = styled.span`
  color: #dc2626;
  font-size: 0.85rem;
`

export const SubmitWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 1rem;

  button {
    width: 100%;
    max-width: 320px;
  }
`

export const SpinnerWrapper = styled.div`
  width: 100%;
  min-height: 60vh;
  display: flex;
  align-items: center;
  justify-content: center;
`

export const StateBox = styled.div`
  width: 100%;
  max-width: 540px;
  background-color: #ffffff;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  padding: 2rem 1.5rem;
  text-align: center;

  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  margin: 3rem auto 0;

  h2 {
    font-size: 1.25rem;
    color: ${theme.colors.tertiary};
    font-weight: 700;
  }

  p {
    color: #4b5563;
    line-height: 1.5;
  }
`
